import React, { useEffect, useMemo, useState } from 'react'
import {
    Button,
    Card,
    DatePicker,
    Empty,
    Flex,
    Input,
    Pagination,
    Popover,
    Segmented,
    Skeleton,
    Tag,
    Tooltip,
    Typography,
} from 'antd'
import {
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    SolutionOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import dayjs, { Dayjs } from 'dayjs'
import { useAuth } from '../../auth'
import { UPDATE_SITE } from '../../user/permission'
import { useAnswers, useSiteChat, useSiteChats } from '../hooks'
import { ChatFilters } from '../api'
import { PaginationResponse } from '../../zod/pagination'
import { ChatCard, ChatMessage, SiteAnswer, WebChat } from '../site'
import { AnswerForm } from './answer-form'
import { QuestionQueue } from './question-queue'

export type SiteInboxProps = {
    siteId: string
    filters: ChatFilters
    onFiltersChange: (filters: ChatFilters) => void
    /** Searches the chats, or the unanswered questions when those show. */
    onSearchChange: (search?: string) => void
    chatId?: string
    onChatChange: (chatId: string) => void
    /** The unanswered questions as a queue instead of the chats. */
    questions: boolean
    onShowQuestions: () => void
    question?: string
    onQuestionChange: (question?: string) => void
    days: 7 | 30
    /** Recommending vacancies and applying only happen on a website with vacancies. */
    hasVacancies: boolean
}

export function SiteInbox({
    siteId,
    filters,
    onFiltersChange,
    onSearchChange,
    chatId,
    onChatChange,
    questions,
    onShowQuestions,
    question,
    onQuestionChange,
    days,
    hasVacancies,
}: SiteInboxProps) {
    const { t } = useTranslation()
    const { data, isLoading, isFetching } = useSiteChats(siteId, filters)
    const [search, setSearch] = useState(filters.search ?? '')

    useEffect(() => {
        const timer = setTimeout(() => {
            if ((filters.search ?? '') !== search) {
                onSearchChange(search || undefined)
            }
        }, 400)

        return () => clearTimeout(timer)
    }, [search, filters.search, onSearchChange])

    const changeView = (value: string) => {
        if (value === 'questions') {
            onShowQuestions()

            return
        }

        onFiltersChange({
            ...filters,
            outcome: value === 'all' ? undefined : value,
            page: 1,
        })
    }

    return (
        <Flex
            vertical
            gap="small"
        >
            <Flex
                gap="small"
                wrap
                align="center"
            >
                <Input.Search
                    allowClear
                    placeholder={questions ? t('Search in questions') : t('Search in chats')}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="max-w-xs"
                />
                <Segmented
                    value={questions ? 'questions' : (filters.outcome ?? 'all')}
                    onChange={(value) => changeView(String(value))}
                    options={[
                        {
                            label: t('All'),
                            value: 'all',
                        },
                        {
                            label: t('Unanswered questions'),
                            value: 'questions',
                        },
                        ...(hasVacancies
                            ? [
                                  {
                                      label: t('Recommended'),
                                      value: 'recommended',
                                  },
                                  {
                                      label: t('Clicked apply'),
                                      value: 'applied',
                                  },
                              ]
                            : []),
                    ]}
                />
                {!questions && (
                    <PeriodFilter
                        filters={filters}
                        onFiltersChange={onFiltersChange}
                    />
                )}
            </Flex>

            {questions ? (
                <QuestionQueue
                    siteId={siteId}
                    days={days}
                    search={filters.search}
                    question={question}
                    onQuestionChange={onQuestionChange}
                />
            ) : (
                <ChatInbox
                    siteId={siteId}
                    chats={data}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    selected={chatId ?? data?.data[0]?.id ?? null}
                    onChatChange={onChatChange}
                    onPageChange={(page) => onFiltersChange({ ...filters, page })}
                />
            )}
        </Flex>
    )
}

type ChatInboxProps = {
    siteId: string
    chats?: PaginationResponse<WebChat>
    isLoading: boolean
    isFetching: boolean
    selected: string | null
    onChatChange: (chatId: string) => void
    onPageChange: (page: number) => void
}

/** On wide screens an inbox: the list and the chat each scroll on their own, so a chat opens next to it. */
function ChatInbox({ siteId, chats, isLoading, isFetching, selected, onChatChange, onPageChange }: ChatInboxProps) {
    const { t } = useTranslation()
    const isRefreshing = isFetching && !isLoading
    const hasMorePages = chats && chats.meta.lastPage > 1

    return (
        <div className="grid gap-4 lg:h-[75vh] lg:min-h-[480px] lg:grid-cols-[320px_minmax(0,1fr)]">
            <div className="flex min-h-0 flex-col">
                <Card
                    size="small"
                    loading={isLoading}
                    styles={{ body: { padding: 0 } }}
                    className={`min-h-0 overflow-y-auto lg:flex-1 ${isRefreshing ? 'opacity-70' : ''}`}
                >
                    {chats?.data.length === 0 && (
                        <Empty
                            className="py-8"
                            description={t('No chats found')}
                        />
                    )}
                    {chats?.data.map((chat) => (
                        <ChatRow
                            key={chat.id}
                            chat={chat}
                            selected={chat.id === selected}
                            onClick={() => onChatChange(chat.id)}
                        />
                    ))}
                </Card>
                {hasMorePages && (
                    <Pagination
                        className="mt-3 shrink-0"
                        size="small"
                        current={chats.meta.currentPage}
                        total={chats.meta.total}
                        pageSize={chats.meta.perPage}
                        showSizeChanger={false}
                        onChange={onPageChange}
                    />
                )}
            </div>
            <Transcript
                siteId={siteId}
                chatId={selected}
            />
        </div>
    )
}

function PeriodFilter({ filters, onFiltersChange }: Pick<SiteInboxProps, 'filters' | 'onFiltersChange'>) {
    const { t } = useTranslation()
    const active = filters.from || filters.to

    const range: [Dayjs | null, Dayjs | null] = [
        filters.from ? dayjs(filters.from) : null,
        filters.to ? dayjs(filters.to) : null,
    ]

    const dates = [filters.from && dayjs(filters.from).format('D MMM'), filters.to && dayjs(filters.to).format('D MMM')]

    return (
        <Popover
            trigger="click"
            placement="bottomLeft"
            content={
                <DatePicker.RangePicker
                    allowEmpty={[true, true]}
                    value={range}
                    onChange={(picked) =>
                        onFiltersChange({
                            ...filters,
                            from: picked?.[0]?.format('YYYY-MM-DD'),
                            to: picked?.[1]?.format('YYYY-MM-DD'),
                            page: 1,
                        })
                    }
                />
            }
        >
            <Button
                type={active ? 'default' : 'text'}
                icon={<CalendarOutlined />}
            >
                {active ? dates.filter(Boolean).join(' – ') : t('Period')}
            </Button>
        </Popover>
    )
}

const CHAT_ROW_CLASS = 'cursor-pointer border-b border-l-2 border-b-neutral-100 px-4 py-3 last:border-b-0'

type ChatRowProps = {
    chat: WebChat
    selected: boolean
    onClick: () => void
}

function ChatRow({ chat, selected, onClick }: ChatRowProps) {
    const { t } = useTranslation()
    const stateClass = selected ? 'border-l-primary bg-primary/5' : 'border-l-transparent hover:bg-neutral-50'

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(event) => event.key === 'Enter' && onClick()}
            className={`${CHAT_ROW_CLASS} ${stateClass}`}
        >
            <Flex
                justify="space-between"
                gap="small"
            >
                <Typography.Text
                    ellipsis
                    strong={selected}
                    className="min-w-0"
                >
                    {chat.firstQuestion}
                </Typography.Text>
                <Typography.Text
                    type="secondary"
                    className="shrink-0 text-xs"
                >
                    {chat.lastMessageAt && shortTime(chat.lastMessageAt, t)}
                </Typography.Text>
            </Flex>
            <Flex
                gap={10}
                className="mt-1 text-sm"
            >
                {(chat.unansweredCount ?? 0) > 0 && (
                    <Tooltip title={t('Not answered')}>
                        <ExclamationCircleOutlined className="text-orange-500" />
                    </Tooltip>
                )}
                {(chat.recommendationCount ?? 0) > 0 && (
                    <Tooltip title={t('Recommended a vacancy')}>
                        <SolutionOutlined className="text-blue-500" />
                    </Tooltip>
                )}
                {(chat.applyClickCount ?? 0) > 0 && (
                    <Tooltip title={t('Clicked apply')}>
                        <CheckCircleOutlined className="text-green-600" />
                    </Tooltip>
                )}
                <Typography.Text
                    type="secondary"
                    className="text-xs"
                >
                    {t('{{count}} questions', { count: chat.visitorMessageCount })}
                </Typography.Text>
            </Flex>
        </div>
    )
}

type TranscriptProps = {
    siteId: string
    chatId: string | null
    answerable?: boolean
}

/**
 * One chat, start to end. Answerable: a link under unanswered messages to add an answer.
 */
export function Transcript({ siteId, chatId, answerable = true }: TranscriptProps) {
    const { can } = useAuth()
    const { data: chat, isLoading } = useSiteChat(siteId, chatId)
    const { data: answers } = useAnswers(siteId)

    // The message an answer is being added under, and the ones answered here (also when the question was changed).
    const [answering, setAnswering] = useState<string | null>(null)
    const [saved, setSaved] = useState<string[]>([])

    useEffect(() => setAnswering(null), [chatId])

    const answered = useMemo(() => answeredQuestionKeys(answers ?? []), [answers])
    const messages = useMemo(() => chat?.messages ?? [], [chat])

    if (chatId === null) {
        return null
    }

    if (isLoading || !chat) {
        return (
            <Card>
                <Skeleton active />
            </Card>
        )
    }

    const clicked = chat.clickedIds ?? []
    const canAddAnswers = answerable && can(UPDATE_SITE)

    return (
        <Card
            styles={{
                body: {
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    height: '100%',
                },
            }}
            className="min-h-0 min-w-0 lg:h-full"
        >
            <TranscriptHeader chat={chat} />
            <Flex
                vertical
                gap="small"
                className="max-h-[70vh] min-h-0 overflow-y-auto bg-neutral-50 px-4 py-4 lg:max-h-none lg:flex-1"
            >
                {messages.map((message, index) => {
                    const asked = questionBefore(messages, index)

                    return (
                        <TranscriptEntry
                            key={message.id}
                            siteId={siteId}
                            message={message}
                            previous={messages[index - 1]}
                            asked={asked}
                            clickedIds={clicked}
                            answerAdded={answered.has(questionKey(asked)) || saved.includes(message.id)}
                            answering={answering === message.id}
                            onAddAnswer={canAddAnswers ? () => setAnswering(message.id) : undefined}
                            onAnswerSaved={() => {
                                setSaved((current) => [...current, message.id])
                                setAnswering(null)
                            }}
                            onAnswerCancel={() => setAnswering(null)}
                        />
                    )
                })}
            </Flex>
        </Card>
    )
}

/** When the chat was, how long it took, on which page, and what it brought. */
function TranscriptHeader({ chat }: { chat: WebChat }) {
    const { t } = useTranslation()
    const messages = chat.messages ?? []
    const clicked = chat.clickedIds ?? []

    // Recommended vacancies; a page card is part of an answer.
    const recommended = new Set(
        messages.flatMap((message) => message.cards.filter((card) => card.type === 'vacancy').map((card) => card.id))
    ).size

    const minutes = chat.lastMessageAt ? Math.max(0, dayjs(chat.lastMessageAt).diff(chat.createdAt, 'minute')) : 0

    return (
        <Flex
            gap="middle"
            wrap
            className="shrink-0 border-b border-neutral-100 px-4 py-3 text-sm text-neutral-500"
        >
            <span>
                <ClockCircleOutlined className="mr-1" />
                {dayLabel(chat.createdAt, t)} {dayjs(chat.createdAt).format('HH:mm')} ·{' '}
                {minutes < 1 ? t('less than a minute') : t('{{count}} min', { count: minutes })}
            </span>
            {chat.pageUrl && (
                <Typography.Link
                    href={chat.pageUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm"
                >
                    <FileTextOutlined className="mr-1" />
                    {chat.pageTitle ?? new URL(chat.pageUrl).pathname}
                </Typography.Link>
            )}
            {recommended > 0 && (
                <span>
                    <SolutionOutlined className="mr-1" />
                    {t('{{count}} vacancies recommended', { count: recommended })}
                </span>
            )}
            {clicked.length > 0 && (
                <span className="text-green-700">
                    <CheckCircleOutlined className="mr-1" />
                    {t('Clicked apply')}
                </span>
            )}
        </Flex>
    )
}

type TranscriptEntryProps = {
    siteId: string
    message: ChatMessage
    previous?: ChatMessage
    /** The visitor's question this message answers. */
    asked: string
    clickedIds: string[]
    answerAdded: boolean
    answering: boolean
    onAddAnswer?: () => void
    onAnswerSaved: () => void
    onAnswerCancel: () => void
}

/** One message in a transcript, after the day when a new day starts. */
function TranscriptEntry({
    siteId,
    message,
    previous,
    asked,
    clickedIds,
    answerAdded,
    answering,
    onAddAnswer,
    onAnswerSaved,
    onAnswerCancel,
}: TranscriptEntryProps) {
    const { t } = useTranslation()
    const newDay = !previous || !dayjs(previous.createdAt).isSame(message.createdAt, 'day')
    const showsDay = newDay && previous !== undefined
    const showsLabel = !previous || previous.role !== message.role || newDay

    return (
        <>
            {showsDay && (
                <Typography.Text
                    type="secondary"
                    className="self-center text-xs"
                >
                    {dayLabel(message.createdAt, t)}
                </Typography.Text>
            )}
            <TranscriptMessage
                observer
                message={message}
                showLabel={showsLabel}
                clickedIds={clickedIds}
                answerAdded={answerAdded}
                onAddAnswer={onAddAnswer}
                answerForm={
                    answering && (
                        <AnswerForm
                            siteId={siteId}
                            question={asked}
                            submitLabel={t('Save')}
                            cancelLabel={t('Cancel')}
                            onSaved={onAnswerSaved}
                            onCancel={onAnswerCancel}
                        />
                    )
                }
            />
        </>
    )
}

export type TranscriptMessageProps = {
    message: Pick<ChatMessage, 'role' | 'content' | 'answered' | 'cards'>
    /** Reading someone else's chat (the inbox): visitor on the left. Otherwise the visitor is "me", on the right. */
    observer?: boolean
    showLabel?: boolean
    clickedIds?: string[]
    /** An answer was added for the visitor's question since. */
    answerAdded?: boolean
    onAddAnswer?: () => void
    /** The form to add an answer, open under this message. */
    answerForm?: React.ReactNode
}

const UNANSWERED_BUBBLE = 'border border-orange-200 bg-orange-50 text-orange-950'

/** The bubble per message role, when reading someone else's chat (observer) or taking part in it. */
const BUBBLES = {
    observer: {
        visitor: 'border border-neutral-200 bg-white',
        assistant: 'bg-blue-50 text-blue-950',
    },
    participant: {
        visitor: 'bg-primary text-white',
        assistant: 'bg-neutral-100',
    },
}

export function TranscriptMessage({
    message,
    observer = false,
    showLabel = false,
    clickedIds = [],
    answerAdded = false,
    onAddAnswer,
    answerForm,
}: TranscriptMessageProps) {
    const { t } = useTranslation()
    const visitor = message.role === 'visitor'
    const unanswered = !visitor && message.answered === false
    const right = observer ? !visitor : visitor
    const view = observer ? 'observer' : 'participant'
    const bubble = unanswered ? UNANSWERED_BUBBLE : BUBBLES[view][message.role]
    const canAddAnswer = onAddAnswer && !answerForm

    return (
        <Flex
            vertical
            gap={4}
            align={right ? 'flex-end' : 'flex-start'}
        >
            {observer && showLabel && (
                <Typography.Text
                    type="secondary"
                    className="px-1 text-xs"
                >
                    {visitor ? t('Visitor') : 'Evaa'}
                </Typography.Text>
            )}
            <div className={`max-w-[80%] whitespace-pre-wrap rounded-xl px-3 py-2 ${bubble}`}>{message.content}</div>
            {unanswered && (
                <Flex
                    gap="small"
                    align="center"
                >
                    <Tag
                        color="orange"
                        className="m-0"
                    >
                        {t('Not on the website')}
                    </Tag>
                    {answerAdded ? (
                        <Typography.Text className="text-xs text-green-700">
                            <CheckCircleOutlined className="mr-1" />
                            {t('Answer added')}
                        </Typography.Text>
                    ) : (
                        canAddAnswer && (
                            <Button
                                type="link"
                                size="small"
                                className="px-0"
                                onClick={onAddAnswer}
                            >
                                {t('Add answer')}
                            </Button>
                        )
                    )}
                </Flex>
            )}
            {answerForm && (
                <div className="w-full max-w-[80%] rounded-xl border border-neutral-200 bg-white p-3">{answerForm}</div>
            )}
            {message.cards.length > 0 && (
                <Cards
                    cards={message.cards}
                    clickedIds={clickedIds}
                />
            )}
        </Flex>
    )
}

type CardsProps = {
    cards: ChatCard[]
    clickedIds: string[]
}

function Cards({ cards, clickedIds }: CardsProps) {
    const { t } = useTranslation()

    return (
        <Flex
            vertical
            gap={4}
            className="max-w-[80%]"
        >
            {cards.map((card) => (
                <div
                    key={`${card.type}:${card.id}`}
                    className="rounded-lg border border-neutral-200 bg-white px-3 py-2"
                >
                    <Flex
                        justify="space-between"
                        gap="middle"
                    >
                        <Typography.Link
                            href={card.url}
                            target="_blank"
                            rel="noreferrer"
                            strong
                        >
                            {card.title}
                        </Typography.Link>
                        {clickedIds.includes(card.id) && (
                            <Typography.Text className="shrink-0 text-xs text-green-700">
                                <CheckCircleOutlined className="mr-1" />
                                {t('Clicked apply')}
                            </Typography.Text>
                        )}
                    </Flex>
                    <Typography.Text
                        type="secondary"
                        className="text-sm"
                    >
                        {card.details.join(' · ')}
                    </Typography.Text>
                </div>
            ))}
        </Flex>
    )
}

/** The last thing the visitor asked before the message at this index. */
function questionBefore(messages: ChatMessage[], index: number): string {
    const visitorMessage = [...messages.slice(0, index)].reverse().find((item) => item.role === 'visitor')

    return visitorMessage?.content ?? ''
}

/** An answer covers its question, and the visitor's own words when the question was tidied up. */
function answeredQuestionKeys(answers: SiteAnswer[]): Set<string> {
    const questions = answers.flatMap((answer) => [answer.question, answer.askedQuestion])

    return new Set(questions.filter((question): question is string => !!question).map(questionKey))
}

type Translate = (key: string, options?: Record<string, unknown>) => string

function dayLabel(date: Date, t: Translate): string {
    const day = dayjs(date)

    if (day.isSame(dayjs(), 'day')) {
        return t('Today')
    }

    if (day.isSame(dayjs().subtract(1, 'day'), 'day')) {
        return t('Yesterday')
    }

    return day.format(day.isSame(dayjs(), 'year') ? 'D MMM' : 'D MMM YYYY')
}

function shortTime(date: Date, t: Translate): string {
    return dayjs(date).isSame(dayjs(), 'day') ? dayjs(date).format('HH:mm') : dayLabel(date, t)
}

/** Same as SiteAnswer::key on the server: no case or punctuation, to recognise the same question. */
function questionKey(question: string): string {
    return question
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim()
}
